import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '../../services/authService';
import { apiJson, apiResponse } from './apiFetch';
import type { VolunteerResourceDto } from '@signalvote/shared';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

// ============================================================================
// Types
// ============================================================================

export interface VolunteerResourcesData {
  quickTips: string;
  script: string;
}

export type VolunteerResourceType = 'quickTips' | 'script';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch both quick tips and script volunteer resources
 */
export function useVolunteerResources() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.volunteerResources.all,
    queryFn: async (): Promise<VolunteerResourcesData> => {
      const [quickTipsResponse, scriptResponse] = await Promise.all([
        apiResponse('/api/volunteerresources/QuickTips'),
        apiResponse('/api/volunteerresources/Script'),
      ]);

      if (!quickTipsResponse.ok || !scriptResponse.ok) {
        throw new Error('Failed to fetch volunteer resources');
      }

      const [quickTipsData, scriptData] = await Promise.all([
        quickTipsResponse.json() as Promise<VolunteerResourceDto>,
        scriptResponse.json() as Promise<VolunteerResourceDto>,
      ]);

      return {
        quickTips: quickTipsData.content || '',
        script: scriptData.content || '',
      };
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Update a volunteer resource (quickTips or script)
 */
export function useUpdateVolunteerResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resourceType,
      content,
    }: {
      resourceType: VolunteerResourceType;
      content: string;
    }) => {
      const apiResourceType = resourceType === 'quickTips' ? 'QuickTips' : 'Script';
      return apiJson<VolunteerResourceDto>(`/api/volunteerresources/${apiResourceType}`, {
        method: 'PUT',
        json: { content },
        error: (body) => new Error(body.message || 'Failed to update resource'),
      });
    },
    onSuccess: (_, variables) => {
      // Update the cache with the new content
      queryClient.setQueryData<VolunteerResourcesData>(
        queryKeys.volunteerResources.all,
        (old) =>
          old
            ? {
                ...old,
                [variables.resourceType]: variables.content,
              }
            : undefined
      );
      // Also invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.volunteerResources.all });
    },
  });
}
